/** Shared purchase-order types for client + server summaries. */

import type { MfgVendorExtraContact } from "./vendorExtraContacts.js";

export type { MfgVendorExtraContact };

export type MfgPurchaseOrderStatus =
  | "draft"
  | "issued"
  | "confirmed"
  | "partially_received"
  | "received"
  | "cancelled";

export type MfgPoOrigin = "purchaser" | "plant_request" | "prediction";

export type MfgPoAttachmentKind =
  | "signed_po"
  | "confirmation"
  | "shipping_invoice"
  | "other";

export type MfgVendor = {
  id: string;
  organization_id: string;
  name: string;
  primary_contact_name: string;
  phone: string;
  email: string;
  address: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  extra_contacts?: MfgVendorExtraContact[];
};

export type MfgPurchasingSettings = {
  next_po_number: number;
  po_number_prefix: string;
  default_confirmation_message: string;
  default_po_cc_emails: string[];
  default_invoice_cc_emails: string[];
};

export const DEFAULT_PO_CONFIRMATION_MESSAGE =
  "Please send us an order confirmation including quantities, pricing, an estimated shipping date, and a reference to our purchase order number. Thank you for your business.";

export const DEFAULT_PURCHASING_SETTINGS: MfgPurchasingSettings = {
  next_po_number: 1,
  po_number_prefix: "",
  default_confirmation_message: DEFAULT_PO_CONFIRMATION_MESSAGE,
  default_po_cc_emails: [],
  default_invoice_cc_emails: [],
};

export type MfgPurchaseOrderLine = {
  id: string;
  po_id: string;
  sort_order: number;
  material_id: string | null;
  material_name: string;
  description: string;
  quantity: number;
  rate: number | null;
  amount: number | null;
  expected_pieces: number | null;
  received_qty: number | null;
  quality_note: string;
  received_at: string | null;
  /** ok = accept into on-hand; off_quality = chargeback / follow-up, no on-hand. */
  quality_status?: "ok" | "off_quality" | "pending";
  chargeback_usd?: number | null;
  /** Set when this line is packed for one building. */
  unit_id?: string | null;
};

export type MfgPurchaseOrderAttachment = {
  id: string;
  po_id: string;
  kind: MfgPoAttachmentKind;
  storage_bucket: string;
  storage_path: string;
  content_type: string;
  byte_size: number;
  original_filename: string;
  uploaded_by_user_id: string | null;
  created_at: string;
};

export type MfgPurchaseOrder = {
  id: string;
  organization_id: string;
  plant_id: string;
  vendor_id: string | null;
  po_number: string;
  status: MfgPurchaseOrderStatus;
  po_date: string;
  vendor_name: string;
  vendor_phone: string;
  vendor_email: string;
  vendor_address: string;
  company_name: string;
  company_logo_url: string;
  company_billing_address: string;
  company_phone: string;
  company_email: string;
  purchaser_user_id: string | null;
  purchaser_name: string;
  purchaser_phone: string;
  purchaser_email: string;
  ship_to_override: string;
  /** Why this PO was archived. Empty unless status is cancelled. */
  cancel_reason?: string;
  confirmation_message: string;
  /** Printed on the PO PDF as a comma-delimited CC list. */
  cc_emails?: string[];
  /** Extra CC emails for this PO's vendor invoice. */
  invoice_cc_emails?: string[];
  subtotal: number | null;
  total: number | null;
  approved_by_user_id: string | null;
  approved_by_name: string;
  approved_at: string | null;
  /** Set by Finalize. Materials, quantities, and header stay editable until then. */
  finalized_at?: string | null;
  finalized_by_user_id?: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  /** purchaser = desk draft; plant_request = floor shortage; prediction = outlook draft. */
  origin?: MfgPoOrigin;
  request_note?: string;
  acknowledged_at?: string | null;
  acknowledged_by_user_id?: string | null;
  /** Expected arrival told to the plant at issue (not invoice delivery). */
  expected_delivery_on?: string | null;
  /** Child vendor PO split from a plant_request. */
  source_request_id?: string | null;
  lines?: MfgPurchaseOrderLine[];
  attachments?: MfgPurchaseOrderAttachment[];
  /** Present on list responses so the queue can flag missing shipping invoices. */
  attachment_kinds?: MfgPoAttachmentKind[];
  plant_name?: string;
  plant_address?: string;
  plant_phone?: string;
  plant_email?: string;
  /** Vendor invoice amount (Y). When set, this is the PO total. */
  invoice_total_usd?: number | null;
  /** Free-text net terms typed at invoice receipt. */
  invoice_pay_terms?: string;
  invoice_delivery_on?: string | null;
  invoice_recorded_at?: string | null;
  /** Rollup of line chargebacks (delivery confirm). */
  chargeback_total_usd?: number | null;
};

const OPEN_PO_EDIT_STATUSES = new Set<MfgPurchaseOrderStatus>(["draft", "issued", "confirmed"]);

/** True once purchasing finalized the PO, or receiving / archive has started. */
export function purchaseOrderContentLocked(order: {
  status: MfgPurchaseOrderStatus;
  finalized_at?: string | null;
}): boolean {
  if (order.finalized_at) return true;
  return !OPEN_PO_EDIT_STATUSES.has(order.status);
}

/** Draft, issued, and confirmed orders can be finalized once. Receiving locks edits on its own. */
export function purchaseOrderCanFinalize(order: {
  status: MfgPurchaseOrderStatus;
  finalized_at?: string | null;
}): boolean {
  return !order.finalized_at && OPEN_PO_EDIT_STATUSES.has(order.status);
}

export function formatPoNumber(prefix: string, n: number): string {
  const p = (prefix ?? "").trim();
  return p ? `${p}${n}` : String(n);
}

export function lineAmount(quantity: number, rate: number | null | undefined): number | null {
  if (rate == null || !Number.isFinite(rate)) return null;
  if (!Number.isFinite(quantity)) return null;
  return Math.round(quantity * rate * 100) / 100;
}

export function sumLineAmounts(
  lines: ReadonlyArray<{ amount: number | null | undefined }>
): number | null {
  let any = false;
  let sum = 0;
  for (const line of lines) {
    if (line.amount == null || !Number.isFinite(line.amount)) continue;
    any = true;
    sum += line.amount;
  }
  return any ? Math.round(sum * 100) / 100 : null;
}
